import { keepPreviousData, queryOptions } from '@tanstack/react-query'

import { apiClient } from './client'
import type {
  AccountHold,
  CloseSavingsAccountRequest,
  CreateSavingsProductRequest,
  DormancyRun,
  InterestRun,
  IssuePassbookRequest,
  LoanPassbook,
  LoanPrerequisites,
  OpenSavingsAccountRequest,
  PlaceHoldRequest,
  PostInterestRequest,
  ReactivateAccountRequest,
  ReleaseHoldRequest,
  RunDormancyRequest,
  SavingsAccount,
  SavingsAccountStatus,
  SavingsMovementRequest,
  SavingsPosting,
  SavingsProduct,
  SavingsProductClass,
  SavingsTransaction,
  SavingsTransferRequest,
} from './savings-types'
import type { DualControlResult, PagedResult } from './types'

/**
 * The savings endpoints.
 *
 * Three things are settled here rather than in a screen.
 *
 * **Numbers are normalised at this boundary.** The contract types every integral field as
 * `["integer", "string"]`, because the API's source-generated serialiser may emit either. A screen
 * that received `"250000"` where it expected `250000` would right-align it, compare it, and add it
 * to a total — all of which do the wrong thing silently. Identifiers become strings and amounts
 * become numbers here, once, exactly as `reference-api.ts` already does for branch ids.
 *
 * **Dual control is visible in the return types.** `postWithdrawal`, `closeSavingsAccount`, and
 * `createSavingsProduct` answer with a {@link DualControlResult} rather than the thing that was
 * asked for. `awaitingApproval: true` means the command is stored and *nothing has changed*, so a
 * caller says "sent for approval" and never "done".
 *
 * **Two of these commands answer with nothing at all.** Releasing a hold and reactivating an
 * account are 204s; their callers refetch the account, which is where the effect actually shows.
 */

type Numeric = number | string

const asCount = (value: Numeric): number => (typeof value === 'number' ? value : Number(value))

/** Minor units. The same normalisation as a count, named separately because the units matter. */
const asMinor = (value: Numeric): number => asCount(value)

const asId = (value: Numeric): string => String(value)

/* ------------------------------------------------------------------ wire shapes */

/*
 * These mirror the response schemas before normalisation. They are type aliases rather than
 * interfaces on purpose: an interface here would be a second declaration of a shape the drift
 * gate already checks under its real name, and two declarations of one payload is how the two
 * drift apart.
 */

type AccountHoldWire = Omit<AccountHold, 'amountMinor'> & { amountMinor: Numeric }

type SavingsAccountWire = Omit<
  SavingsAccount,
  'branchId' | 'ledgerBalanceMinor' | 'holdAmountMinor' | 'availableBalanceMinor' | 'accruedInterestMinor' | 'holds'
> & {
  branchId: Numeric
  ledgerBalanceMinor: Numeric
  holdAmountMinor: Numeric
  availableBalanceMinor: Numeric
  accruedInterestMinor: Numeric
  holds: AccountHoldWire[]
}

type SavingsProductWire = Omit<
  SavingsProduct,
  'interestRatePerAnnum' | 'minOpeningBalanceMinor' | 'minBalanceMinor' | 'withdrawalFeeMinor' | 'dormancyDays'
> & {
  interestRatePerAnnum: Numeric
  minOpeningBalanceMinor: Numeric
  minBalanceMinor: Numeric
  withdrawalFeeMinor: Numeric | null
  dormancyDays: Numeric
}

type SavingsTransactionWire = Omit<SavingsTransaction, 'amountMinor' | 'balanceAfterMinor'> & {
  amountMinor: Numeric
  balanceAfterMinor: Numeric
}

type SavingsPostingWire = Omit<
  SavingsPosting,
  'transaction' | 'balanceAfterMinor' | 'availableBalanceMinor'
> & {
  transaction: SavingsTransactionWire
  balanceAfterMinor: Numeric
  availableBalanceMinor: Numeric
}

type LoanPrerequisitesWire = Omit<LoanPrerequisites, 'securityDepositBalanceMinor'> & {
  securityDepositBalanceMinor: Numeric
}

type InterestRunWire = Omit<InterestRun, 'accountsPosted' | 'totalPostedMinor'> & {
  accountsPosted: Numeric
  totalPostedMinor: Numeric
}

type DormancyRunWire = Omit<DormancyRun, 'accountsAged'> & { accountsAged: Numeric }

type PageWire<T> = { items: T[]; page: Numeric; pageSize: Numeric; totalCount: Numeric; totalPages: Numeric }

/* ------------------------------------------------------------------ normalisers */

const toHold = (wire: AccountHoldWire): AccountHold => ({
  ...wire,
  amountMinor: asMinor(wire.amountMinor),
})

const toAccount = (wire: SavingsAccountWire): SavingsAccount => ({
  ...wire,
  branchId: asId(wire.branchId),
  ledgerBalanceMinor: asMinor(wire.ledgerBalanceMinor),
  holdAmountMinor: asMinor(wire.holdAmountMinor),
  availableBalanceMinor: asMinor(wire.availableBalanceMinor),
  accruedInterestMinor: asMinor(wire.accruedInterestMinor),
  // Defended rather than trusted: `holds` is required by the contract, and an account panel that
  // maps over `undefined` takes the whole screen down instead of one card.
  holds: Array.isArray(wire.holds) ? wire.holds.map(toHold) : [],
})

const toProduct = (wire: SavingsProductWire): SavingsProduct => ({
  ...wire,
  // Left as the fraction the server serves. Converting to a percentage here would put a display
  // decision in the transport, and the request half of the same field is a fraction too.
  interestRatePerAnnum: asCount(wire.interestRatePerAnnum),
  minOpeningBalanceMinor: asMinor(wire.minOpeningBalanceMinor),
  minBalanceMinor: asMinor(wire.minBalanceMinor),
  withdrawalFeeMinor: wire.withdrawalFeeMinor === null ? null : asMinor(wire.withdrawalFeeMinor),
  dormancyDays: asCount(wire.dormancyDays),
})

const toTransaction = (wire: SavingsTransactionWire): SavingsTransaction => ({
  ...wire,
  amountMinor: asMinor(wire.amountMinor),
  balanceAfterMinor: asMinor(wire.balanceAfterMinor),
})

const toPosting = (wire: SavingsPostingWire): SavingsPosting => ({
  ...wire,
  transaction: toTransaction(wire.transaction),
  balanceAfterMinor: asMinor(wire.balanceAfterMinor),
  availableBalanceMinor: asMinor(wire.availableBalanceMinor),
})

const toPage = <TWire, TItem>(wire: PageWire<TWire>, item: (row: TWire) => TItem): PagedResult<TItem> => ({
  items: Array.isArray(wire.items) ? wire.items.map(item) : [],
  page: asCount(wire.page),
  pageSize: asCount(wire.pageSize),
  totalCount: asCount(wire.totalCount),
  totalPages: asCount(wire.totalPages),
})

/* ------------------------------------------------------------------ queries */

export interface SavingsAccountListQuery {
  customerId?: string
  status?: SavingsAccountStatus
  productClass?: SavingsProductClass
  /** Free text over the account number, customer number, and customer name. */
  search?: string
  branchId?: string
  page?: number
  pageSize?: number
}

export interface SavingsStatementQuery {
  /** ISO date, inclusive. Omitted means from the account's first movement. */
  from?: string
  to?: string
  page?: number
  pageSize?: number
}

export interface SavingsProductListQuery {
  productClass?: SavingsProductClass
  /** Retired products are excluded unless this is set — they still carry live accounts. */
  includeInactive?: boolean
}

export const savingsKeys = {
  all: ['savings'] as const,
  accounts: () => [...savingsKeys.all, 'account'] as const,
  accountLists: () => [...savingsKeys.accounts(), 'list'] as const,
  accountList: (query: SavingsAccountListQuery) => [...savingsKeys.accountLists(), query] as const,
  account: (accountId: string) => [...savingsKeys.accounts(), accountId] as const,
  statements: (accountId: string) => [...savingsKeys.account(accountId), 'statement'] as const,
  statement: (accountId: string, query: SavingsStatementQuery) =>
    [...savingsKeys.statements(accountId), query] as const,
  products: () => [...savingsKeys.all, 'product'] as const,
  productList: (query: SavingsProductListQuery) => [...savingsKeys.products(), 'list', query] as const,
  passbooks: (customerId: string) => [...savingsKeys.all, 'passbook', customerId] as const,
  prerequisites: (customerId: string, requiredSecurityDepositMinor: number | null) =>
    [...savingsKeys.all, 'loan-prerequisites', customerId, requiredSecurityDepositMinor] as const,
}

export const savingsAccountListQueryOptions = (query: SavingsAccountListQuery) =>
  queryOptions({
    queryKey: savingsKeys.accountList(query),
    queryFn: async () => {
      const response = await apiClient.get<PageWire<SavingsAccountWire>>('/savings/accounts', {
        params: query,
      })
      return toPage(response.data, toAccount)
    },
    // Paging and typing keep the page that is on screen while the next one is fetched. A table of
    // balances that blanks on every keystroke cannot be read while it is being filtered.
    placeholderData: keepPreviousData,
  })

export const savingsAccountQueryOptions = (accountId: string) =>
  queryOptions({
    queryKey: savingsKeys.account(accountId),
    queryFn: async () => {
      const response = await apiClient.get<SavingsAccountWire>(
        `/savings/accounts/${encodeURIComponent(accountId)}`,
      )
      return toAccount(response.data)
    },
    // A balance is the one figure nothing should be decided against stale — a hold placed at
    // another counter a minute ago changes what may be withdrawn now.
    staleTime: 0,
  })

export const savingsStatementQueryOptions = (accountId: string, query: SavingsStatementQuery) =>
  queryOptions({
    queryKey: savingsKeys.statement(accountId, query),
    queryFn: async () => {
      const response = await apiClient.get<PageWire<SavingsTransactionWire>>(
        `/savings/accounts/${encodeURIComponent(accountId)}/transactions`,
        { params: query },
      )
      return toPage(response.data, toTransaction)
    },
    placeholderData: keepPreviousData,
  })

export const savingsProductListQueryOptions = (query: SavingsProductListQuery = {}) =>
  queryOptions({
    queryKey: savingsKeys.productList(query),
    queryFn: async () => {
      const response = await apiClient.get<SavingsProductWire[]>('/savings/products', { params: query })
      return Array.isArray(response.data) ? response.data.map(toProduct) : []
    },
    // Products change when somebody countersigns a change to them, which is rare.
    staleTime: 10 * 60 * 1000,
  })

/**
 * Every book issued to a customer, replaced ones included.
 *
 * The list is **branch-scoped**: the server filters it to the branches the caller can reach. The
 * uniqueness rules behind `IssuePassbook` are not — a serial is unique across the institution, and
 * so is the rule that a customer holds one active book. So an empty list here is not proof that
 * the customer holds no book, and any screen offering "issue a book" off the back of it has to say
 * so rather than treating a refusal as a surprise.
 */
export const customerPassbooksQueryOptions = (customerId: string) =>
  queryOptions({
    queryKey: savingsKeys.passbooks(customerId),
    queryFn: async () => {
      const response = await apiClient.get<LoanPassbook[]>(
        `/savings/customers/${encodeURIComponent(customerId)}/passbooks`,
      )
      return Array.isArray(response.data) ? response.data : []
    },
  })

/**
 * The three gates a loan account cannot be opened without.
 *
 * `requiredSecurityDepositMinor` is the product's requirement, and without it the deposit gate is
 * answered against nothing — pass it whenever the loan product is known.
 */
export const loanPrerequisitesQueryOptions = (
  customerId: string,
  requiredSecurityDepositMinor: number | null = null,
) =>
  queryOptions({
    queryKey: savingsKeys.prerequisites(customerId, requiredSecurityDepositMinor),
    queryFn: async () => {
      const response = await apiClient.get<LoanPrerequisitesWire>(
        `/savings/customers/${encodeURIComponent(customerId)}/loan-prerequisites`,
        {
          params:
            requiredSecurityDepositMinor === null
              ? undefined
              : { requiredSecurityDepositMinor },
        },
      )

      return {
        ...response.data,
        securityDepositBalanceMinor: asMinor(response.data.securityDepositBalanceMinor),
        savingsAccount: response.data.savingsAccount,
        securityDepositAccount: response.data.securityDepositAccount,
        unmetGates: Array.isArray(response.data.unmetGates) ? response.data.unmetGates : [],
      } satisfies LoanPrerequisites
    },
    staleTime: 0,
  })

/* ------------------------------------------------------------------ commands */

export async function openSavingsAccount(request: OpenSavingsAccountRequest): Promise<SavingsAccount> {
  const response = await apiClient.post<SavingsAccountWire>('/savings/accounts', request)
  return toAccount(response.data)
}

/**
 * Cash in over the counter.
 *
 * Not dual-controlled, and not therefore unconditional: the posting runs through the caller's own
 * open till, so it can be refused with `teller.no_open_till`, `teller.till_cash_limit_exceeded`, or
 * a 403 for `teller.deposit.post` even from someone holding `savings.deposit.post`.
 */
export async function postDeposit(
  accountId: string,
  request: SavingsMovementRequest,
): Promise<SavingsPosting> {
  const response = await apiClient.post<SavingsPostingWire>(
    `/savings/accounts/${encodeURIComponent(accountId)}/deposits`,
    request,
  )
  return toPosting(response.data)
}

/**
 * Cash out over the counter. **Dual control**, and the response says nothing about the posting.
 *
 * Even an executed withdrawal answers with `DualControlResult` — `awaitingApproval`,
 * `approvalRequestId`, `entityId` and nothing else — so there is no transaction number, no balance
 * after, and no available balance to read back to the customer. There is no endpoint for a single
 * savings transaction either; the row is recovered by refetching the statement. Any withdrawal fee
 * the product carries posts as a *second* statement line with its own transaction number, which
 * appears in no response at all.
 *
 * One further consequence worth stating where it is decided: when this is queued, the cash leg
 * runs against the **maker's** till at the moment of countersignature, not the approver's. If the
 * original teller has closed their session by then, the countersigned withdrawal fails with
 * `teller.no_open_till` and the failure surfaces in the approvals inbox rather than here.
 */
export async function postWithdrawal(
  accountId: string,
  request: SavingsMovementRequest,
): Promise<DualControlResult> {
  const response = await apiClient.post<DualControlResult>(
    `/savings/accounts/${encodeURIComponent(accountId)}/withdrawals`,
    request,
  )
  return response.data
}

/** Book-to-book between two customer accounts. Touches no cash drawer, so no till is required. */
export async function postTransfer(
  accountId: string,
  request: SavingsTransferRequest,
): Promise<SavingsPosting> {
  const response = await apiClient.post<SavingsPostingWire>(
    `/savings/accounts/${encodeURIComponent(accountId)}/transfers`,
    request,
  )
  return toPosting(response.data)
}

/**
 * Cash collateral into a security deposit account. Cash in through the till, like a deposit.
 *
 * Posted against the wrong class of account — a deposit aimed at a security deposit account, or a
 * top-up aimed at a regular savings one — the server answers `savings.account_wrong_class`.
 */
export async function topUpSecurityDeposit(
  accountId: string,
  request: SavingsMovementRequest,
): Promise<SavingsPosting> {
  const response = await apiClient.post<SavingsPostingWire>(
    `/savings/accounts/${encodeURIComponent(accountId)}/security-deposit`,
    request,
  )
  return toPosting(response.data)
}

/**
 * Earmarks part of a balance.
 *
 * **This amends rather than adds.** The aggregate looks for an active hold with the same reason
 * and revises it in place — the amount and the note are overwritten and `placedAtUtc` is left
 * alone. So the cap is not `ledgerBalance - holdAmount`: it is that plus whatever the existing
 * hold of this reason already holds, which is what makes increasing an existing lien possible.
 */
export async function placeAccountHold(
  accountId: string,
  request: PlaceHoldRequest,
): Promise<AccountHold> {
  const response = await apiClient.post<AccountHoldWire>(
    `/savings/accounts/${encodeURIComponent(accountId)}/holds`,
    request,
  )
  return toHold(response.data)
}

/** 204. The caller refetches the account, which is where the available balance changes. */
export async function releaseAccountHold(holdId: string, request: ReleaseHoldRequest): Promise<void> {
  await apiClient.post(`/savings/holds/${encodeURIComponent(holdId)}/release`, request)
}

/** 204. Refused with `savings.account_not_dormant` on an account that is not dormant. */
export async function reactivateSavingsAccount(
  accountId: string,
  request: ReactivateAccountRequest,
): Promise<void> {
  await apiClient.post(`/savings/accounts/${encodeURIComponent(accountId)}/reactivate`, request)
}

/**
 * **Dual control.** Settles the account and refunds its balance — and the refund is a cash payout
 * through the till, `CashDirection.Out`, before the status changes. For a security deposit account
 * this is the path by which collateral is handed back over a counter.
 */
export async function closeSavingsAccount(
  accountId: string,
  request: CloseSavingsAccountRequest,
): Promise<DualControlResult> {
  const response = await apiClient.post<DualControlResult>(
    `/savings/accounts/${encodeURIComponent(accountId)}/close`,
    request,
  )
  return response.data
}

/**
 * **Dual control.** Sets the rate, the fees, the dormancy period, and the general-ledger accounts
 * every account written against the product will carry.
 */
export async function createSavingsProduct(
  request: CreateSavingsProductRequest,
): Promise<DualControlResult> {
  const response = await apiClient.post<DualControlResult>('/savings/products', request)
  return response.data
}

export async function issuePassbook(
  customerId: string,
  request: IssuePassbookRequest,
): Promise<LoanPassbook> {
  const response = await apiClient.post<LoanPassbook>(
    `/savings/customers/${encodeURIComponent(customerId)}/passbooks`,
    request,
  )
  return response.data
}

/** Capitalises accrued interest for one product at one branch. Posts a journal. */
export async function postSavingsInterest(request: PostInterestRequest): Promise<InterestRun> {
  const response = await apiClient.post<InterestRunWire>('/savings/interest/run', request)
  return {
    ...response.data,
    accountsPosted: asCount(response.data.accountsPosted),
    totalPostedMinor: asMinor(response.data.totalPostedMinor),
  }
}

/** Ages idle accounts into dormancy at a branch. Moves no money. */
export async function runDormancySweep(request: RunDormancyRequest): Promise<DormancyRun> {
  const response = await apiClient.post<DormancyRunWire>('/savings/dormancy/run', request)
  return { ...response.data, accountsAged: asCount(response.data.accountsAged) }
}
